import { Injectable, inject } from '@angular/core';
import { AppCheck, getToken, onTokenChanged } from '@angular/fire/app-check';
import { waitForRecaptchaEnterprise } from '../../app-check.bootstrap';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AppCheckService {
  private appCheck = inject(AppCheck, { optional: true });
  private warmInFlight: Promise<void> | null = null;

  constructor() {
    if (!environment.firebaseAppCheckDebug || !this.appCheck) return;
    onTokenChanged(this.appCheck, () => {
      console.info('[App Check] Token de depuración activo.');
    });
  }

  /**
   * Best-effort token warm-up. Never blocks Auth (Google/email).
   * Repeated 403s throttle requests for 24h — avoid retries and forceRefresh.
   */
  warmToken(): void {
    if (!this.appCheck) return;
    this.warmInFlight ??= this.fetchTokenOnce().finally(() => {
      this.warmInFlight = null;
    });
    void this.warmInFlight;
  }

  private async fetchTokenOnce(): Promise<void> {
    if (!this.appCheck) return;

    const usesEnterprise = !environment.firebaseAppCheckDebug && !environment.appCheckDebugToken?.trim();
    if (usesEnterprise) {
      try {
        await waitForRecaptchaEnterprise();
      } catch (err) {
        console.warn('[App Check] reCAPTCHA Enterprise:', err);
        return;
      }
    }

    try {
      await getToken(this.appCheck, false);
    } catch (err) {
      logAppCheckFailure(err);
    }
  }
}

function logAppCheckFailure(err: unknown): void {
  const code = (err as { code?: string })?.code ?? '';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';

  if (code === 'appCheck/throttled') {
    console.warn(
      `[App Check] Peticiones bloqueadas 24h por un 403 anterior (appCheck/throttled). ` +
      `El login con Google/email puede seguir. Para recuperar App Check: borra datos del sitio ` +
      `(${host}) en el navegador o espera 24h. Corrige la causa del 403 (debug token / dominio en reCAPTCHA).`,
    );
    return;
  }

  if (environment.firebaseAppCheckDebug) {
    console.warn(
      '[App Check] DEV local: registra el "AppCheck debug token" de la consola en Firebase → App Check.',
      err,
    );
    return;
  }

  if (environment.envName === 'test' && !environment.appCheckDebugToken) {
    console.warn(
      `[App Check] TEST (${host}): sin token. Opción A — reCAPTCHA Enterprise: dominio "${host}" en la site key + Auth authorized domains. ` +
      `Opción B — en secrets.env pon STAGING_APP_CHECK_DEBUG_TOKEN (crear en Firebase → App Check → debug tokens) y redeploy.`,
      err,
    );
    return;
  }

  console.warn(`[App Check] Sin token en ${host}.`, err);
}
